import { OrderStatus } from "@classes-service-types/order-status";
import { validate as validateUUID } from "uuid";
import { computed, type Ref, ref, watch, watchEffect } from "vue";
import {
  type LocationQuery,
  type NavigationFailure,
  type RouteLocationAsRelativeGeneric,
  useRoute,
  useRouter,
} from "vue-router";
import { requiredSchool } from "@/constants.ts";
import ApplicationProcess from "../apply/ApplicationProcess.vue";
import { LOGIN_REDIRECT_QUERY, RegistrationStage } from "../constants";
import ConfiguratorProcess from "../personalize/ConfiguratorProcess.vue";
import { buildOrderQueryParams, getSingleParam } from "../utils/utils";
import { useApplicationSteps } from "./useApplicationSteps";
import { useRegistrationOptions } from "./useRegistrationOptions";

const withoutLoginRedirectQuery = (query: LocationQuery): LocationQuery => {
  const { [LOGIN_REDIRECT_QUERY]: _loginRedirect, ...filteredQuery } = query;
  return filteredQuery;
};
const withSortedDefinedEntries = (values?: Record<string, unknown>) => {
  return Object.fromEntries(
    Object.entries(values ?? {})
      .filter(([, value]) => value !== undefined)
      .sort(([left], [right]) => left.localeCompare(right)),
  );
};

export const useApplicationRouting = (
  application: Record<string, any>,
  existingApplication?: { isResolvingExistingApplication?: Ref<boolean> },
) => {
  const route = useRoute();
  const router = useRouter();
  const { applicationId } = useRegistrationOptions();
  const { firstIncompleteStep, stepsResolutionStatus } = useApplicationSteps(computed(() => application));

  const hadLoginRedirectQuery = ref(false);

  const isRoutingFromLogin = computed<boolean>(() => hadLoginRedirectQuery.value);

  const sessionIsResolved = computed<boolean>(() => Boolean(application.session.value));
  const ordersAreResolved = computed<boolean>(
    () => !applicationId.value || Array.isArray(application.applicationOrders.value),
  );
  const configuratorStatusIsResolved = computed<boolean>(
    () => !applicationId.value || !application.applicationIsLoading?.value,
  );
  const stepsAreResolved = computed<boolean>(() => stepsResolutionStatus.value === "ready");
  const existingApplicationIsResolved = computed<boolean>(
    () => !existingApplication?.isResolvingExistingApplication?.value,
  );

  const routingInputsAreResolved = computed<boolean>(
    () =>
      sessionIsResolved.value &&
      ordersAreResolved.value &&
      configuratorStatusIsResolved.value &&
      stepsAreResolved.value &&
      existingApplicationIsResolved.value,
  );

  const defaultLearnerTypeId = computed<string | undefined>(() => {
    return application?.profile?.value?.learner_type_id;
  });

  const learnerTypeId = computed<string | undefined>(() => {
    const id = getSingleParam(route.query?.learnerType);
    return id && validateUUID(id) ? id : defaultLearnerTypeId.value;
  });

  const step = ref<string | undefined>(getSingleParam(route.params?.step));

  // DATSC-1048: when a routable order carries a home institution but the active
  // learner type no longer supports a college, the student must re-select a
  // program. Gate on resolved inputs and a resolved learner type so we never
  // fire on a transiently-undefined learner type (e.g. before the profile loads
  // on an AP-5336 login-redirect resume), which would yank a returning student
  // off the step they left off on.
  const shouldForceProgramStep = computed<boolean>(() => {
    const learnerType = learnerTypeId.value;
    return (
      routingInputsAreResolved.value &&
      Boolean(application.primaryOrder.value?.home_institution_id) &&
      learnerType !== undefined &&
      !requiredSchool.includes(learnerType)
    );
  });

  watch(
    () => [shouldForceProgramStep.value, route.params?.step],
    ([force]) => {
      if (force) {
        step.value = "program";
        if (getSingleParam(route.params?.step) !== "program") {
          // Preserve the existing query (session-id, learnerType, etc.) — omitting
          // it makes vue-router resolve the target with an empty query and strip it.
          router.replace({ params: { ...route.params, step: "program" }, query: route.query });
        }
      } else {
        // Don't clobber the current step to undefined when the learner-type change flips
        // the force off on a URL that carries no step param (e.g. the Program confirmation
        // form) — keep the step the student is on rather than bouncing them off it.
        step.value = getSingleParam(route.params?.step) ?? step.value;
      }
    },
    { immediate: true },
  );

  const stageRequestedInUrl = computed<RegistrationStage | undefined>(
    () => RegistrationStage[getSingleParam(route.params?.stage)?.toUpperCase() as keyof typeof RegistrationStage],
  );

  const isReady = computed<boolean>(
    () =>
      !application.isLoading.value ||
      Array.isArray(application.applicationOrders.value) ||
      Boolean(application.session.value),
  );
  const isPersonalizedSession = computed(() => Boolean(application.session.value?.is_personalized));

  const permittedStages = computed<RegistrationStage[]>(() => {
    if (!isReady.value) return [];

    if (isPersonalizedSession.value) {
      if (!application.isConfiguratorComplete.value) {
        return [RegistrationStage.PERSONALIZE];
      }
      return [RegistrationStage.PERSONALIZE, RegistrationStage.APPLY];
    }
    // TODO: Handle external link registration type
    return [RegistrationStage.APPLY];
  });

  const stage = computed<RegistrationStage | undefined>(() => {
    if (permittedStages.value.length === 0) {
      return undefined; // Not ready
    }
    const nextPermittedStage = (): RegistrationStage => {
      let nextStage = permittedStages.value[0];
      if (permittedStages.value.length > 1 && application.isConfiguratorComplete.value) {
        nextStage = RegistrationStage.APPLY;
      }
      return nextStage;
    };
    if (!stageRequestedInUrl.value) {
      return nextPermittedStage();
    }
    if (isRoutingFromLogin.value) {
      // Regardless of the url parameters, send to the last permitted stage
      // i.e. the furthest along
      return nextPermittedStage();
    } else if (permittedStages.value.includes(stageRequestedInUrl.value)) {
      return stageRequestedInUrl.value;
    }
    return nextPermittedStage();
  });

  const component = computed(() => {
    return {
      personalize: ConfiguratorProcess,
      apply: ApplicationProcess,
    }[stage.value ?? RegistrationStage.PERSONALIZE];
  });

  const renderComponent = computed<boolean>(() => isReady.value && application.session.value && component.value);

  const routeToPersonalize = (): RouteLocationAsRelativeGeneric => {
    return {
      name: route.name,
      params: {
        ...route.params,
        stage: RegistrationStage.PERSONALIZE,
        step: undefined,
      },
      query: {
        ...withoutLoginRedirectQuery(route.query),
        ...buildOrderQueryParams(application.primaryOrder.value),
      },
    };
  };

  const routeToApply = (): RouteLocationAsRelativeGeneric => {
    const applyStepSlug =
      applicationId.value && stepsResolutionStatus.value === "ready" && !firstIncompleteStep.value
        ? "next-steps"
        : firstIncompleteStep.value?.slug;

    const lastStep: string | undefined = applicationId.value ? applyStepSlug : undefined;
    const params = { ...route.params, stage: stage.value, step: applyStepSlug };

    if (!lastStep) {
      delete params.step;
    }

    return {
      name: route.name,
      query: undefined,
      params,
    };
  };

  const resolvePersonalizeOrApply = (): RouteLocationAsRelativeGeneric => {
    const isReturnFromSaveForLater: boolean =
      !application.isConfiguratorComplete.value && stage.value === RegistrationStage.PERSONALIZE;
    return isReturnFromSaveForLater ? routeToPersonalize() : routeToApply();
  };

  const isEquivalentRoute = (target: RouteLocationAsRelativeGeneric): boolean => {
    return (
      JSON.stringify({
        name: target.name ?? route.name,
        params: withSortedDefinedEntries(target.params as Record<string, unknown> | undefined),
        query: withSortedDefinedEntries(target.query as Record<string, unknown> | undefined),
      }) ===
      JSON.stringify({
        name: route.name,
        params: withSortedDefinedEntries(route.params as Record<string, unknown> | undefined),
        query: withSortedDefinedEntries(route.query as Record<string, unknown> | undefined),
      })
    );
  };

  const clearLoginRedirectState = () => {
    hadLoginRedirectQuery.value = false;
  };

  const handleRouterUpdates = async (): Promise<NavigationFailure | void | undefined> => {
    const targetRoute = resolvePersonalizeOrApply();
    if (isEquivalentRoute(targetRoute)) {
      clearLoginRedirectState();
      return;
    }

    const navigationResult = await router.replace(targetRoute);
    if (!navigationResult) {
      clearLoginRedirectState();
    }
    return navigationResult;
  };

  watchEffect(async () => {
    const order = application.primaryOrder.value;
    if (order && ![OrderStatus.Draft, OrderStatus.Submitted].includes(order.status)) {
      await router.push({
        name: "experience-detail",
        params: {
          applicationId: applicationId.value,
        },
      });
      return;
    }

    if (!routingInputsAreResolved.value) {
      return;
    }

    // The Program-step force (DATSC-1048) owns navigation when the saved home
    // institution no longer matches the learner type — don't normalize over it,
    // otherwise the AP-5336 resume and the force would compete for the route.
    if (shouldForceProgramStep.value) {
      return;
    }

    if (
      !stageRequestedInUrl.value ||
      !permittedStages.value.includes(stageRequestedInUrl.value) ||
      isRoutingFromLogin.value
    ) {
      await handleRouterUpdates();
    }
  });

  watch(
    () => getSingleParam(route.query?.[LOGIN_REDIRECT_QUERY]),
    (v) => {
      if (v) hadLoginRedirectQuery.value = true;
    },
    { immediate: true },
  );

  return {
    stage,
    step,
    component,
    renderComponent,
    isReady,
    permittedStages,
  };
};
